import { motion } from "motion/react";
import Image from "next/image";
import styled, { css } from "styled-components";
import { useEffect, useMemo, useState } from "react";
import { Swiper, SwiperSlide, useSwiper } from "swiper/react";
import { Navigation } from "swiper/modules";
import { useFloating, useHover, useInteractions, offset, flip, shift, FloatingPortal } from "@floating-ui/react";

// Import Swiper styles
import "swiper/css";
import "swiper/css/navigation";
import NextLazyImage from "@/components/Core/NextLazyImage";

const TimelineWrapper = styled.div`
  width: 100%;

  overflow: hidden;
`;

const TimelineContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-end;
`;

const TimelineItem = styled.div`
  cursor: pointer;
  flex: 0 0 auto;
  user-select: none;
`;

const TimelineItemThumbnail = styled.div<{ $active: boolean; $isLoading?: boolean; $isNavigable?: boolean }>`
  border-radius: 8px;
  width: auto;
  height: 66px;
  aspect-ratio: 16/9;
  position: relative;
  overflow: hidden;
  margin-bottom: 8px;
  margin-right: 8px;
  filter: saturate(0.7) brightness(0.7);
  transition: all 0.3s ease;
  
  ${(props) => {
    if (props.$isLoading) {
      return css`
        filter: saturate(0.3) brightness(0.4);
        opacity: 0.6;
      `;
    }
    if (!props.$isNavigable) {
      return css`
        filter: saturate(0.2) brightness(0.3);
        opacity: 0.4;
        cursor: not-allowed;
      `;
    }
    if (props.$active) {
      return css`
        filter: saturate(1) brightness(1);
      `;
    }
    return css`
      cursor: pointer;
      &:hover {
        filter: saturate(0.8) brightness(0.8);
      }
    `;
  }}
`;

const StyledSwiper = styled(Swiper)`
  padding: 0 20px;
  .swiper-wrapper {
    align-items: flex-end;
  }
`;

const StyledSwiperSlide = styled(SwiperSlide)`
  display: flex;
  justify-content: center;
  align-items: flex-end;
  width: auto;
  position: relative;
  height: 100%;
  padding-bottom: 18px;
`;

const TimelineLine = styled.div`
  width: 100%;
  height: 1px;
  background-color: #525252;
  position: absolute;
  bottom: 16px;
  left: 0;
`;

const TimelineNewYearWrapper = styled.div<{ $isFirst: boolean }>`
  position: absolute;
  bottom: 0px;
  left: -4px;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  pointer-events: none;
  user-select: none;
  flex-direction: column;
  gap: 4px;
  ${(props) =>
    props.$isFirst &&
    css`
      align-items: flex-start;
    `}
`;

const TimelineNewYear = styled.div<{ $isFirst: boolean }>`
  width: 1px;
  height: 8px;
  background-color: #525252;
  ${(props) =>
    props.$isFirst &&
    css`
      margin-left: 20px;
    `}
`;

const YearText = styled.div<{ $isFirst: boolean }>`
  font-size: 11px;
  font-weight: 500;
  color: #ffffffa6;
  font-variant-numeric: tabular-nums;
  ${(props) =>
    props.$isFirst &&
    css`
      margin-left: 8px;
    `}
`;

const Tooltip = styled.div`
  background-color: #08090a;
  color: #f7f8f8;
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  z-index: 1000;
  border-radius: 8px;
  border: 0.5px solid rgba(62, 62, 68, 0.6);
  background: rgba(40, 40, 44, 0.797);
  backdrop-filter: blur(10px);
`;

const LoadingProgress = styled.div<{ progress: number }>`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 0 0 8px 8px;
  overflow: hidden;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: ${props => props.progress}%;
    background: linear-gradient(90deg, #4ade80, #22c55e);
    transition: width 0.3s ease;
  }
`;

const LoadingSpinner = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid #f7f8f8;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  z-index: 1;
  
  @keyframes spin {
    0% { transform: translate(-50%, -50%) rotate(0deg); }
    100% { transform: translate(-50%, -50%) rotate(360deg); }
  }
`;

interface TimelineProps {
  videos: any[];
  currentVideoIndex: number;
  previousVideoIndex: number;
  targetVideoIndex: number;
  transitionDurationMs: number;
  framesPath: string;
  onVideoSelect: (index: number) => void;
  isTransitioning: boolean;
  preloadStatus: any;
}

interface TooltipHoverProps {
  video: any;
  index: number;
  children: React.ReactNode;
  onVideoSelect: (index: number) => void;
  getThumbnailState: (index: number) => string;
  framesPath: string;
  preloadStatus: any;
}

function TooltipHover({ video, index, children, onVideoSelect, getThumbnailState, framesPath, preloadStatus }: TooltipHoverProps) {
  const [isOpen, setIsOpen] = useState(false);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: "top",
    middleware: [offset(8)],
  });

  const hover = useHover(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([hover]);

  // Get loading state for this video
  const videoState = preloadStatus?.videoStates?.[index];
  const isLoading = videoState?.isLoading ?? true;
  const isNavigable = (videoState?.loadedFrames ?? 0) > 0;
  const progress = videoState?.progress ?? 0;

  // Generate tooltip content based on loading state
  const getTooltipContent = () => {
    if (isLoading && progress === 0) {
      return `${video.title} - Loading...`;
    }
    if (isLoading && progress > 0) {
      return `${video.title} - ${progress}% loaded`;
    }
    return video.title;
  };

  return (
    <>
      <TimelineItem
        ref={refs.setReference}
        {...getReferenceProps()}
        onClick={() => {
          if (isNavigable) {
            onVideoSelect(index);
          }
        }}>
        <TimelineItemThumbnail 
          $active={getThumbnailState(index) === "active"}
          $isLoading={isLoading}
          $isNavigable={isNavigable}
        >
          <NextLazyImage
            src={`${framesPath}/${video.thumbnail}`}
            alt=''
            width={118}
            height={66}
            style={{ objectFit: "cover" }}
          />
          {isLoading && progress === 0 && <LoadingSpinner />}
          {isLoading && progress > 0 && <LoadingProgress progress={progress} />}
        </TimelineItemThumbnail>
      </TimelineItem>
      {isOpen && (
        <FloatingPortal>
          <Tooltip ref={refs.setFloating} style={floatingStyles} {...getFloatingProps()}>
            {getTooltipContent()}
          </Tooltip>
        </FloatingPortal>
      )}
    </>
  );
}

export default function Timeline({
  videos,
  currentVideoIndex,
  previousVideoIndex,
  targetVideoIndex,
  transitionDurationMs,
  onVideoSelect,
  framesPath,
  isTransitioning,
  preloadStatus,
}: TimelineProps) {
  const [swiperInstance, setSwiperInstance] = useState<any>(null);
  const [currentSlide, setCurrentSlide] = useState(currentVideoIndex);

  const thumbnailVariants = {
    inactive: {
      width: 86,
      height: 48,
    },
    active: {
      width: 140,
      height: 78,
    },
  };

  const getThumbnailState = (index: number) => {
    if (isTransitioning) {
      if (index === targetVideoIndex) return "active";
      if (index === previousVideoIndex) return "inactive";
      return "inactive";
    }
    return index === currentVideoIndex ? "active" : "inactive";
  };

  // Update currentSlide when targetVideoIndex changes
  useEffect(() => {
    if (targetVideoIndex !== undefined) {
      setCurrentSlide(targetVideoIndex);
    }
  }, [targetVideoIndex]);

  // Initialize currentSlide with currentVideoIndex on mount
  useEffect(() => {
    setCurrentSlide(currentVideoIndex);
  }, [currentVideoIndex]);

  // Scroll to currentSlide when it changes
  /*useEffect(() => {
    if (swiperInstance && currentSlide !== undefined) {
      // Use requestAnimationFrame to ensure slides are rendered
      requestAnimationFrame(() => {
        swiperInstance.slideTo(currentSlide, 500, false);
      });
    }
  }, [swiperInstance, currentSlide]);*/

  useEffect(() => {
    if (swiperInstance) {
      swiperInstance.slideTo(currentSlide, 600, true);
    }
  }, [currentSlide, swiperInstance]);

  // Convert duration from milliseconds to seconds for Framer Motion
  const transitionDurationSeconds = transitionDurationMs / 1000;

  return (
    <TimelineWrapper>
      <StyledSwiper
        modules={[Navigation]}
        spaceBetween={0}
        slidesPerView='auto'
        centeredSlides={false}
        loop={false}
        allowTouchMove={true}
        grabCursor={true}
        observeSlideChildren={false}
        speed={500}
        onSwiper={setSwiperInstance}>
        {videos?.map((video, index) => (
          <StyledSwiperSlide key={index}>
            <TooltipHover
              video={video}
              index={index}
              onVideoSelect={onVideoSelect}
              getThumbnailState={getThumbnailState}
              framesPath={framesPath}
              preloadStatus={preloadStatus}>
              {video.title}
            </TooltipHover>

            <TimelineLineRenderer newYear={video.newYear} year={video.year} index={index} />
          </StyledSwiperSlide>
        ))}
      </StyledSwiper>
    </TimelineWrapper>
  );
}

const TimelineLineRenderer = ({ newYear, year, index }: { newYear: boolean; year: number; index: number }) => {
  if (newYear) {
    return (
      <>
        <TimelineNewYearWrapper $isFirst={index === 0}>
          <TimelineNewYear $isFirst={index === 0} />
          {year && <YearText $isFirst={index === 0}>{year}</YearText>}
        </TimelineNewYearWrapper>
        <TimelineLine />
      </>
    );
  }
  return <TimelineLine />;
};
